// ─────────────────────────────────────────────────────────────────────────────
// Single source for presence status → display (#742), now backed by the server definitions
// (#753): label, colour and order are data editable by the user from presence-status-tab.vue
// (backend/.../BuiltInPresenceStatusKeys.cs for the eight built-in statuses), not a table
// frozen in the frontend. `usePresenceStatusLookup`
// (composables/use-presence-status-lookup.ts) loads those definitions once per session; the
// functions below consult them through `findPresenceStatusDefinition` and only fall back on
// {@link PRESENCE_STATUS_FALLBACK} while no definition is loaded (before the request answers,
// or if it fails) — the charts render synchronously, before the promise resolves.
//
// `Record<PresenceStatus, …>` forces the compiler to refuse an addition to the enum until the
// fallback has its entry here — that is what would have caught the regression of #742 (one of
// the five copies had lost `Unconfirmed`, hence a pie slice with neither colour nor label).
// ─────────────────────────────────────────────────────────────────────────────
import { PresenceStatus } from '@/sdk/model';
import { findPresenceStatusDefinition } from '@/composables/use-presence-status-lookup';

export interface PresenceStatusDisplay {
  /** French label displayed in the legends and charts. */
  label: string;
  /** Couleur hex exploitable telle quelle en CSS (style inline, options ApexCharts). */
  color: string;
  /** Display order in the presence legends and charts (ascending). */
  order: number;
}

/**
 * Synchronous fallback, used while no server definition is loaded. Mirrors
 * `BuiltInPresenceStatusKeys.All` (backend) term for term — label, hex colour and order — so
 * that the administration screen and the charts never diverge again before the first load:
 * this fallback is a safety net, never a second truth.
 */
export const PRESENCE_STATUS_FALLBACK: Record<PresenceStatus, PresenceStatusDisplay> = {
  [PresenceStatus.Unconfirmed]: { label: 'Non confirmé', color: '#9e9e9e', order: 0 },
  [PresenceStatus.Present]: { label: 'Présent', color: '#4caf50', order: 1 },
  [PresenceStatus.JustifiedAbsence]: { label: 'Absence motivée', color: '#2196f3', order: 2 },
  [PresenceStatus.UnjustifiedAbsence]: { label: 'Absence non motivée', color: '#f44336', order: 3 },
  [PresenceStatus.Discharge]: { label: 'Fermeture / libération', color: '#9e9e9e', order: 4 },
  [PresenceStatus.LateArrival]: { label: 'Retard', color: '#ff9800', order: 5 },
  [PresenceStatus.EarlyDeparture]: { label: 'Départ anticipé', color: '#9c27b0', order: 6 },
  [PresenceStatus.ComplementaryService]: { label: 'Services complémentaires', color: '#00897b', order: 7 },
};

/** French label of a presence status — the server definition if loaded, else the fallback, else the raw key. */
export function presenceStatusLabel(status: string): string {
  const def = findPresenceStatusDefinition(status);
  if (def) return def.label;
  return PRESENCE_STATUS_FALLBACK[status as PresenceStatus]?.label ?? status;
}

/** Display order of a presence status — the server definition if loaded, else the fallback, else 99 (end of list). */
export function presenceStatusOrder(status: string): number {
  const def = findPresenceStatusDefinition(status);
  if (def) return def.order;
  return PRESENCE_STATUS_FALLBACK[status as PresenceStatus]?.order ?? 99;
}

/**
 * A colour usable directly in CSS (inline style, ApexCharts options) for a presence status — a
 * hex from the server definition if loaded, otherwise the fallback's. A single output contract
 * whatever the origin: always a hex, never a Vuetify theme token for the caller to resolve.
 * `color` is optional on the server side (a custom status can be created without a colour): we
 * then also fall back rather than rendering a pie slice with no colour.
 */
export function presenceStatusChartColor(status: string): string | undefined {
  const def = findPresenceStatusDefinition(status);
  if (def?.color) return def.color;
  return PRESENCE_STATUS_FALLBACK[status as PresenceStatus]?.color;
}

/**
 * Has a teacher actually ruled on this cell? `isConfirmed` is the server's own word for it
 * (BuiltInPresenceStatusKeys), and `Unconfirmed` is the only built-in that answers no: a draft,
 * a student's self-declaration, a cell pre-seeded from the planning. The wall counts such a row
 * as « À saisir » rather than as a status of its own (#996), which is what it is.
 *
 * Falls back on the key while no definition is loaded, like its neighbours here.
 */
export function isConfirmedStatus(status: string): boolean {
  const def = findPresenceStatusDefinition(status);
  if (def) return def.isConfirmed;
  return status !== PresenceStatus.Unconfirmed;
}
